import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getCandidatureDetail, updateCandidatureStatut, documentUrl, documentsCandidatUrl,
} from '../../services/api';
import { STATUTS, CATEGORIES_SCORE } from '../../constants/formOptions';
import { StatusBadge } from './ApplicationsList';

const Card = ({ title, action, children }) => (
  <section className="detail-card">
    <div className="detail-header">
      <h3>{title}</h3>
      {action}
    </div>
    <div className="detail-body">{children}</div>
  </section>
);

const Item = ({ label, value }) => (
  <div className="detail-item">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value === 0 ? 0 : (value || '—')}</span>
  </div>
);

const Tags = ({ label, items }) => (
  <div className="detail-item" style={{ marginTop: '0.9rem' }}>
    <span className="detail-label">{label}</span>
    {items && items.length > 0 ? (
      <div className="tag-list" style={{ marginTop: '0.3rem' }}>
        {items.map(item => <span key={item} className="tag">{item}</span>)}
      </div>
    ) : <span className="detail-value">—</span>}
  </div>
);

const Checklist = ({ items }) => (
  <ul className="checklist">
    {items.map(([label, value]) => <li key={label} className={value ? 'yes' : 'no'}>{label}</li>)}
  </ul>
);

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [statut, setStatut] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCandidatureDetail(id)
      .then((data) => {
        setC(data);
        setStatut(data.statut);
        setNotes(data.notes_admin || '');
      })
      .catch(() => {
        toast.error('Candidature introuvable.');
        navigate('/recruteur/list');
      });
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await updateCandidatureStatut(id, { statut, notes_admin: notes });
      setC(prev => ({ ...prev, ...data }));
      toast.success('Enregistré.');
    } catch {
      toast.error("L'enregistrement a échoué.");
    } finally {
      setSaving(false);
    }
  };

  if (!c) {
    return <div className="loader"><div className="spinner" /></div>;
  }

  const cat = CATEGORIES_SCORE.find(x => x.value === c.categorie_score);
  const dirty = statut !== c.statut || notes !== (c.notes_admin || '');

  return (
    <>
      <div className="page-head">
        <div>
          <button className="btn btn-ghost btn-sm" style={{ paddingLeft: 0 }} onClick={() => navigate(-1)}>
            ← Retour
          </button>
          <h1>{c.nom} {c.prenoms}</h1>
          <span className="mono muted">{c.numero_dossier}</span>
          <span style={{ marginLeft: '0.6rem' }}><StatusBadge statut={c.statut} /></span>
        </div>
      </div>

      <div className="detail-layout">
        <div>
          <Card title="Identité">
            <div className="detail-grid">
              <Item label="Sexe" value={c.sexe_display} />
              <Item label="Date de naissance" value={new Date(c.date_naissance).toLocaleDateString('fr-FR')} />
              <Item label="Téléphone 1" value={c.telephone} />
              <Item label="Téléphone 2" value={c.telephone_2} />
              <Item label="E-mail" value={c.email} />
            </div>
          </Card>

          <Card title="Formation">
            <div className="detail-grid">
              <Item label="Domaine" value={c.domaine_formation_display} />
              <Item label="Niveau" value={c.niveau_etude_display} />
              <Item label="Diplôme" value={c.diplome_obtenu} />
            </div>
          </Card>

          <Card title="Expérience">
            <div className="detail-grid">
              <Item label="Années d'expérience" value={c.annees_experience} />
              <Item label="Agents encadrés (max.)" value={c.nombre_agents_encadres} />
              <Item label="Opérations sur tablette" value={c.nombre_operations_tablette} />
            </div>
            <Tags label="Rôles tenus" items={c.roles_display} />
            <Tags label="Enquêtes réalisées" items={c.operations_display} />
            <Tags label="Outils de collecte" items={c.outils_display} />
            {c.description_experience && (
              <div className="detail-item" style={{ marginTop: '0.9rem' }}>
                <span className="detail-label">Précisions</span>
                <p className="detail-value" style={{ whiteSpace: 'pre-wrap', fontWeight: 400 }}>{c.description_experience}</p>
              </div>
            )}
          </Card>

          <Card title="Compétences et disponibilité">
            <Checklist items={[
              ['Échantillonnage', c.connaissance_echantillonnage],
              ['Collecte et contrôle qualité', c.connaissance_controle_qualite],
              ['Déploiement des équipes', c.capacite_deploiement_equipes],
              ['Vérification des données', c.capacite_verification_donnees],
              ['Coordination avec les autorités', c.experience_relations_autorites],
              ['Disponible toute la durée', c.disponibilite_totale],
              ['Apte aux déplacements', c.apte_deplacements],
              ['Engagement éthique', c.engagement_ethique],
            ]} />
          </Card>
        </div>

        <aside className="detail-aside">
          <Card title="Score">
            <div className="score-big" style={{ color: cat?.color }}>{c.score}<span className="muted" style={{ fontSize: '1rem' }}> / 100</span></div>
            <div className="muted" style={{ fontSize: '0.82rem', marginTop: '0.3rem' }}>
              {cat?.label}{c.experience_enquetes_sante && ' · expérience santé / vaccination'}
            </div>
          </Card>

          <Card
            title="Documents"
            action={<a className="btn btn-secondary btn-sm" href={documentsCandidatUrl(c.id)}>Tout (ZIP)</a>}
          >
            <ul className="doc-list">
              {(c.documents || []).map(doc => (
                <li key={doc.champ}>
                  <span>{doc.label}</span>
                  {doc.present ? (
                    <span className="doc-actions">
                      <a href={documentUrl(c.id, doc.champ, true)} target="_blank" rel="noopener noreferrer">Voir</a>
                      <a href={documentUrl(c.id, doc.champ)}>Télécharger</a>
                    </span>
                  ) : <span className="muted">absent</span>}
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Décision">
            <div className="form-group">
              <label htmlFor="statut">Statut</label>
              <select id="statut" value={statut} onChange={(e) => setStatut(e.target.value)}>
                {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="notes">Notes internes</label>
              <textarea id="notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSave} disabled={saving || !dirty}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <p className="muted" style={{ fontSize: '0.75rem', marginTop: '0.75rem' }}>
              Reçue le {new Date(c.date_soumission).toLocaleString('fr-FR')}
            </p>
          </Card>
        </aside>
      </div>
    </>
  );
};

export default ApplicationDetail;
